import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { Helmet } from "react-helmet";
import { useSelector } from "react-redux";
import axios from "axios";
import { toast } from "react-toastify";
import {
  FiShield,
  FiTrendingUp,
  FiSun,
  FiUsers,
  FiUser,
  FiMail,
  FiSend,
  FiCheckCircle,
  FiThumbsUp,
  FiSettings,
  FiClock,
  FiTrash2,
  FiRefreshCw,
  FiCheck,
} from "react-icons/fi";
import { FaStar } from "react-icons/fa";
import Header from "../../components/layouts/Header";
import Footer from "../../components/layouts/Footer";
import ContactStrip from "./ContactStrip";
import { Baseurl } from "../../config/BaseUrl";
import "./FooterPages.css";

const RATINGS = [
  { value: 1, label: "Poor" },
  { value: 2, label: "Fair" },
  { value: 3, label: "Good" },
  { value: 4, label: "Very Good" },
  { value: 5, label: "Excellent" },
];

const RATING_DETAILS = {
  1: { emoji: "😞", label: "Poor", desc: "Needs major improvement in search or functionality" },
  2: { emoji: "😐", label: "Fair", desc: "Average experience, some issues encountered" },
  3: { emoji: "🙂", label: "Good", desc: "Satisfactory experience, met expectations" },
  4: { emoji: "😊", label: "Very Good", desc: "Smooth experience with great listings" },
  5: { emoji: "🤩", label: "Excellent", desc: "Outstanding platform, love the UI and features!" },
};

const QUICK_TAGS = [
  "⚡ Fast & Smooth UI",
  "🔍 Property Search Accuracy",
  "📱 Mobile Responsive",
  "🏡 Verified Listings",
  "💬 Helpful Support",
  "⭐ Clean Modern Design",
  "💰 Pricing Transparency",
  "⏱ Fast Page Loading",
];

const FEEDBACK_STORAGE_KEY = "mgh_user_feedbacks";

const RequestaProblem = () => {
  const authState = useSelector((state) => state.Athentication);
  const { loginData, isAuth } = authState || {};

  const [formData, setFormData] = useState({
    name: "",
    email: "",
    rating: 5,
    category: "I have a suggestion",
    feedback: "",
  });

  const [selectedTags, setSelectedTags] = useState([]);
  const [hoverRating, setHoverRating] = useState(0);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [lastSubmission, setLastSubmission] = useState(null);
  const [feedbackHistory, setFeedbackHistory] = useState([]);

  // Auto-fill logged in user details if available
  useEffect(() => {
    if (loginData?.name || loginData?.email) {
      setFormData((prev) => ({
        ...prev,
        name: prev.name || loginData.name || "",
        email: prev.email || loginData.email || "",
      }));
    }
  }, [loginData]);

  // Load previous local feedback history
  useEffect(() => {
    try {
      const stored = localStorage.getItem(FEEDBACK_STORAGE_KEY);
      if (stored) {
        setFeedbackHistory(JSON.parse(stored));
      }
    } catch (e) {
      console.warn("Could not load feedback history from storage");
    }
  }, []);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleRatingClick = (val) => {
    setFormData((prev) => ({ ...prev, rating: val }));
  };

  const handleTagToggle = (tag) => {
    setSelectedTags((prev) => {
      if (prev.includes(tag)) {
        return prev.filter((t) => t !== tag);
      } else {
        return [...prev, tag];
      }
    });
  };

  const handleClearHistory = () => {
    localStorage.removeItem(FEEDBACK_STORAGE_KEY);
    setFeedbackHistory([]);
    toast.info("Feedback history cleared");
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!formData.name.trim()) {
      toast.error("Please enter your name");
      return;
    }
    if (!formData.email.trim() || !formData.email.includes("@")) {
      toast.error("Please enter a valid email address");
      return;
    }
    if (!formData.feedback.trim()) {
      toast.error("Please share your feedback or experience");
      return;
    }

    setIsSubmitting(true);

    const refNumber = `MGH-FB-${Math.floor(100000 + Math.random() * 900000)}`;
    const submissionDate = new Date().toLocaleString("en-IN", {
      dateStyle: "medium",
      timeStyle: "short",
    });

    const newRecord = {
      id: refNumber,
      date: submissionDate,
      name: formData.name.trim(),
      email: formData.email.trim(),
      rating: formData.rating,
      category: formData.category,
      tags: selectedTags,
      feedback: formData.feedback.trim(),
      status: "Received",
    };

    // Prepare real API payload
    const payload = {
      name: formData.name.trim(),
      email: formData.email.trim(),
      mobile: loginData?.mobile || "N/A",
      location: `[Rating: ${formData.rating}/5 - ${RATING_DETAILS[formData.rating].label}] [Category: ${formData.category}] [Ref: ${refNumber}] [Tags: ${selectedTags.join(", ") || "None"}]`,
      city: formData.feedback.trim(),
    };

    try {
      // 1. Send to backend GoogleData/Feedback API
      try {
        await axios.post(`${Baseurl}/api/v1/googledata/new`, payload, {
          headers: { "Content-Type": "application/json" },
          timeout: 4000,
        });
      } catch (err1) {
        // Fallback to local API server if remote is unreachable
        try {
          await axios.post(`http://localhost:4000/api/v1/googledata/new`, payload, {
            headers: { "Content-Type": "application/json" },
            timeout: 2500,
          });
        } catch (err2) {
          console.log("Feedback saved locally:", newRecord);
        }
      }

      // 2. Persist in local storage
      const updatedHistory = [newRecord, ...feedbackHistory.slice(0, 4)];
      setFeedbackHistory(updatedHistory);
      try {
        localStorage.setItem(FEEDBACK_STORAGE_KEY, JSON.stringify(updatedHistory));
      } catch (err) { }

      // 3. Update dynamic state
      setLastSubmission(newRecord);
      setIsSubmitted(true);

      toast.success("Thank you! Your feedback has been received.", {
        position: "top-center",
        autoClose: 3500,
      });
    } catch (err) {
      toast.error("An error occurred. Please try again.");
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleResetForAnother = () => {
    setFormData((prev) => ({
      name: loginData?.name || prev.name,
      email: loginData?.email || prev.email,
      rating: 5,
      category: "I have a suggestion",
      feedback: "",
    }));
    setSelectedTags([]);
    setIsSubmitted(false);
    setLastSubmission(null);
  };

  const activeRating = hoverRating || formData.rating;
  const currentRatingDetail = RATING_DETAILS[activeRating] || RATING_DETAILS[5];

  return (
    <div className="mgh-info-page-wrapper">
      <Helmet>
        <title>We Value Your Feedback | My Golden Homes</title>
        <meta
          name="description"
          content="Share your experience and feedback to help us build a better real estate experience for everyone."
        />
      </Helmet>

      {/* Unchanged Header */}
      <Header />

      {/* Hero Banner */}
      <section className="mgh-info-hero">
        <div className="mgh-info-hero-inner">
          <div className="mgh-info-hero-left">
            <div className="mgh-info-breadcrumb">
              <Link to="/">Home</Link>
              <span>&gt;</span>
              <span>Feedback</span>
            </div>
            <h1 className="mgh-info-hero-title">
              We Value <span>Your Feedback</span>
            </h1>
            <p className="mgh-info-hero-subtitle">
              Help us improve and serve you better. Your feedback inspires us to create a better real estate experience across India.
            </p>

            <div className="mgh-hero-pills-row">
              <div className="mgh-hero-pill">
                <div className="mgh-hero-pill-icon">
                  <FiShield />
                </div>
                <div className="mgh-hero-pill-text">
                  <strong>Better</strong>
                  <span>Services</span>
                </div>
              </div>

              <div className="mgh-hero-pill">
                <div className="mgh-hero-pill-icon">
                  <FiTrendingUp />
                </div>
                <div className="mgh-hero-pill-text">
                  <strong>Improved</strong>
                  <span>Experience</span>
                </div>
              </div>

              <div className="mgh-hero-pill">
                <div className="mgh-hero-pill-icon">
                  <FiSun />
                </div>
                <div className="mgh-hero-pill-text">
                  <strong>Your Voice</strong>
                  <span>Matters</span>
                </div>
              </div>

              <div className="mgh-hero-pill">
                <div className="mgh-hero-pill-icon">
                  <FiUsers />
                </div>
                <div className="mgh-hero-pill-text">
                  <strong>Stronger</strong>
                  <span>Community</span>
                </div>
              </div>
            </div>
          </div>

          <div className="mgh-info-hero-art">
            <img
              src="/assets/images/villa_header_banner.jpg"
              alt="My Golden Homes Feedback"
              onError={(e) => {
                e.target.onerror = null;
                e.target.src = "/assets/images/why_choose_villa.jpg";
              }}
            />
          </div>
        </div>
      </section>

      {/* Main Container */}
      <div className="mgh-info-container">
        <div className="mgh-feedback-grid">
          {/* Left Highlights Box */}
          <div className="mgh-feedback-left-art">
            <div
              style={{
                width: "72px",
                height: "72px",
                borderRadius: "50%",
                background: "#ffffff",
                boxShadow: "0 8px 20px rgba(2, 132, 199, 0.15)",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                margin: "0 auto 16px",
                color: "#0284c7",
                fontSize: "32px",
              }}
            >
              <FiSun />
            </div>

            <div
              style={{
                display: "inline-flex",
                gap: "4px",
                color: "#f59e0b",
                fontSize: "18px",
                marginBottom: "10px",
              }}
            >
              {[1, 2, 3, 4, 5].map((s) => (
                <FaStar key={s} />
              ))}
            </div>

            <h4>Your Voice Shapes Our Platform</h4>
            <p>
              Every review, suggestion, and idea is directly sent to our engineering and product teams to elevate property discovery, verified listings, and buyer-seller trust.
            </p>

            <div
              style={{
                marginTop: "20px",
                padding: "14px",
                background: "rgba(255, 255, 255, 0.8)",
                borderRadius: "10px",
                border: "1px solid #bae6fd",
                textAlign: "left",
                fontSize: "12px",
                color: "#475569",
              }}
            >
              <strong style={{ color: "#0369a1", display: "block", marginBottom: "4px" }}>
                🔒 Fast &amp; Confidential
              </strong>
              Your feedback is securely transmitted and treated with complete privacy.
            </div>
          </div>

          {/* Right Dynamic Form or Success View */}
          <div className="mgh-form-card">
            {isSubmitted && lastSubmission ? (
              /* Dynamic Success Confirmation State */
              <div className="mgh-feedback-success-card">
                <div className="mgh-feedback-success-icon">
                  <FiCheck />
                </div>
                <h3>Thank you, {lastSubmission.name}!</h3>
                <p>
                  Your feedback has been received and logged under reference <strong>{lastSubmission.id}</strong>. We truly appreciate you taking the time to help us grow!
                </p>

                <div className="mgh-feedback-summary-box">
                  <div className="mgh-feedback-summary-row">
                    <span className="label">Reference ID:</span>
                    <strong>{lastSubmission.id}</strong>
                  </div>
                  <div className="mgh-feedback-summary-row">
                    <span className="label">Rating Given:</span>
                    <strong>
                      {lastSubmission.rating} / 5 ({RATING_DETAILS[lastSubmission.rating].label})
                    </strong>
                  </div>
                  <div className="mgh-feedback-summary-row">
                    <span className="label">Category:</span>
                    <strong>{lastSubmission.category}</strong>
                  </div>
                  {lastSubmission.tags && lastSubmission.tags.length > 0 && (
                    <div className="mgh-feedback-summary-row">
                      <span className="label">Highlights:</span>
                      <strong>{lastSubmission.tags.join(", ")}</strong>
                    </div>
                  )}
                  <div className="mgh-feedback-summary-row">
                    <span className="label">Submitted:</span>
                    <strong>{lastSubmission.date}</strong>
                  </div>
                </div>

                <div className="mgh-success-actions-row">
                  <button
                    type="button"
                    onClick={handleResetForAnother}
                    className="mgh-btn-submit-primary"
                    style={{ width: "auto", padding: "10px 20px" }}
                  >
                    <FiRefreshCw />
                    <span>Submit Another Response</span>
                  </button>
                  <Link to="/property-search" className="mgh-btn-secondary">
                    <span>Explore Properties</span>
                  </Link>
                </div>
              </div>
            ) : (
              /* Active Dynamic Feedback Form */
              <>
                <div className="mgh-form-card-header">
                  <h3>Share Your Feedback</h3>
                  <p>
                    We would love to hear from you! Please share your thoughts, ideas or any suggestions.
                  </p>
                </div>

                {/* Logged in indicator if authenticated */}
                {isAuth && loginData?.name && (
                  <div className="mgh-feedback-auth-badge">
                    <span className="mgh-feedback-auth-dot"></span>
                    <span>Logged in as {loginData.name} ({loginData.email || "Verified User"})</span>
                  </div>
                )}

                <form onSubmit={handleSubmit}>
                  <div className="mgh-form-row-2">
                    <div className="mgh-form-group-item">
                      <label>
                        Your Name <span className="req">*</span>
                      </label>
                      <div className="mgh-form-input-wrap">
                        <FiUser className="mgh-form-input-icon" />
                        <input
                          type="text"
                          name="name"
                          value={formData.name}
                          onChange={handleChange}
                          placeholder="Enter your full name"
                          className="mgh-form-input"
                          required
                        />
                      </div>
                    </div>

                    <div className="mgh-form-group-item">
                      <label>
                        Your Email <span className="req">*</span>
                      </label>
                      <div className="mgh-form-input-wrap">
                        <FiMail className="mgh-form-input-icon" />
                        <input
                          type="email"
                          name="email"
                          value={formData.email}
                          onChange={handleChange}
                          placeholder="Enter your email address"
                          className="mgh-form-input"
                          required
                        />
                      </div>
                    </div>
                  </div>

                  {/* Interactive Star Rating */}
                  <div className="mgh-form-group-item">
                    <label>
                      Rate your experience with My Golden Homes <span className="req">*</span>
                    </label>
                    <div className="mgh-stars-row">
                      {RATINGS.map((rate) => {
                        const isFilled = rate.value <= activeRating;
                        return (
                          <button
                            type="button"
                            key={rate.value}
                            className={`mgh-star-btn ${isFilled ? "active" : ""}`}
                            onClick={() => handleRatingClick(rate.value)}
                            onMouseEnter={() => setHoverRating(rate.value)}
                            onMouseLeave={() => setHoverRating(0)}
                            title={rate.label}
                          >
                            <FaStar />
                            <span className="mgh-star-label">{rate.label}</span>
                          </button>
                        );
                      })}
                    </div>

                    {/* Dynamic Rating Insight Badge */}
                    <div className="mgh-rating-desc-badge">
                      <span className="mgh-rating-emoji">{currentRatingDetail.emoji}</span>
                      <div className="mgh-rating-desc-text">
                        <strong>
                          {activeRating} Stars — {currentRatingDetail.label}
                        </strong>
                        <span>{currentRatingDetail.desc}</span>
                      </div>
                    </div>
                  </div>

                  {/* Category Dropdown */}
                  <div className="mgh-form-group-item">
                    <label>
                      Select Feedback Category <span className="req">*</span>
                    </label>
                    <select
                      name="category"
                      value={formData.category}
                      onChange={handleChange}
                      className="mgh-form-select"
                    >
                      <option value="I have a suggestion">I have a suggestion</option>
                      <option value="I want to compliment Mygoldenhomes">I want to compliment Mygoldenhomes</option>
                      <option value="I want to report a problem">I want to report a problem</option>
                      <option value="Customer Support Issue">Customer Support Issue</option>
                      <option value="Listing Verification Inquiry">Listing Verification Inquiry</option>
                      <option value="Other Feedback">Other Feedback</option>
                    </select>
                  </div>

                  {/* Quick Highlight Tags (Clickable Chips) */}
                  <div className="mgh-form-group-item">
                    <label>What highlights stood out to you? (Optional)</label>
                    <div className="mgh-quick-tags-wrap">
                      {QUICK_TAGS.map((tag) => {
                        const isSelected = selectedTags.includes(tag);
                        return (
                          <button
                            type="button"
                            key={tag}
                            className={`mgh-quick-tag ${isSelected ? "active" : ""}`}
                            onClick={() => handleTagToggle(tag)}
                          >
                            {isSelected ? `✓ ${tag}` : tag}
                          </button>
                        );
                      })}
                    </div>
                  </div>

                  {/* Textarea with Dynamic Counter */}
                  <div className="mgh-form-group-item">
                    <label>
                      Your Comments &amp; Feedback <span className="req">*</span>
                    </label>
                    <textarea
                      name="feedback"
                      value={formData.feedback}
                      onChange={handleChange}
                      maxLength={500}
                      placeholder="Tell us what you loved or how we can make your property journey smoother..."
                      className="mgh-form-textarea"
                      rows={4}
                      required
                    />
                    <div
                      className={`mgh-char-counter ${
                        formData.feedback.length >= 500
                          ? "limit"
                          : formData.feedback.length >= 450
                          ? "warning"
                          : ""
                      }`}
                    >
                      <span>
                        {formData.feedback.length >= 500
                          ? "Maximum 500 character limit reached"
                          : formData.feedback.length > 0
                          ? "Helpful feedback is deeply appreciated"
                          : "Please be specific"}
                      </span>
                      <span>{formData.feedback.length} / 500</span>
                    </div>
                  </div>

                  {/* Submit Button with Loading State */}
                  <button
                    type="submit"
                    className="mgh-btn-submit-primary"
                    disabled={isSubmitting}
                  >
                    {isSubmitting ? (
                      <span>Submitting Feedback...</span>
                    ) : (
                      <>
                        <FiSend />
                        <span>Submit Feedback</span>
                      </>
                    )}
                  </button>
                </form>
              </>
            )}
          </div>
        </div>

        {/* Previous Feedback History if available */}
        {feedbackHistory.length > 0 && (
          <div className="mgh-history-section">
            <div className="mgh-history-header">
              <h4>Your Previous Submissions ({feedbackHistory.length})</h4>
              <button
                type="button"
                onClick={handleClearHistory}
                style={{
                  background: "transparent",
                  border: "none",
                  color: "#ef4444",
                  fontSize: "12px",
                  display: "inline-flex",
                  alignItems: "center",
                  gap: "4px",
                  cursor: "pointer",
                  fontWeight: "600",
                }}
              >
                <FiTrash2 />
                <span>Clear</span>
              </button>
            </div>

            <div>
              {feedbackHistory.map((item) => (
                <div key={item.id} className="mgh-history-item">
                  <div className="mgh-history-meta">
                    <span>
                      <strong style={{ color: "#0369a1" }}>{item.id}</strong> • {item.date}
                    </span>
                    <span className="mgh-history-badge">✓ {item.status}</span>
                  </div>
                  <div style={{ display: "flex", gap: "10px", alignItems: "center", marginBottom: "4px" }}>
                    <span style={{ color: "#f59e0b", fontSize: "13px" }}>
                      {"★".repeat(item.rating)}
                      <span style={{ color: "#cbd5e1" }}>{"★".repeat(5 - item.rating)}</span>
                    </span>
                    <span style={{ fontSize: "12px", fontWeight: "600", color: "#334155" }}>
                      {item.category}
                    </span>
                  </div>
                  <p style={{ fontSize: "12.5px", color: "#475569", margin: "0 0 6px 0", lineHeight: "1.4" }}>
                    &ldquo;{item.feedback}&rdquo;
                  </p>
                  {item.tags && item.tags.length > 0 && (
                    <div style={{ display: "flex", flexWrap: "wrap", gap: "4px" }}>
                      {item.tags.map((t, idx) => (
                        <span
                          key={idx}
                          style={{
                            fontSize: "10.5px",
                            background: "#e2e8f0",
                            color: "#334155",
                            padding: "2px 6px",
                            borderRadius: "4px",
                          }}
                        >
                          {t}
                        </span>
                      ))}
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Bottom 4 Feature Cards */}
        <div className="mgh-four-features-grid">
          <div className="mgh-feature-chip">
            <div className="mgh-chip-icon">
              <FiShield />
            </div>
            <div className="mgh-chip-text">
              <strong>Trusted Platform</strong>
              <span>Your feedback helps us maintain quality and trust.</span>
            </div>
          </div>

          <div className="mgh-feature-chip">
            <div className="mgh-chip-icon" style={{ background: "#fef3c7", color: "#d97706" }}>
              <FiSettings />
            </div>
            <div className="mgh-chip-text">
              <strong>Continuous Improvement</strong>
              <span>We use your suggestions to improve our services.</span>
            </div>
          </div>

          <div className="mgh-feature-chip">
            <div className="mgh-chip-icon" style={{ background: "#ecfdf5", color: "#059669" }}>
              <FiUsers />
            </div>
            <div className="mgh-chip-text">
              <strong>Customer Centric</strong>
              <span>Your satisfaction is our priority always.</span>
            </div>
          </div>

          <div className="mgh-feature-chip">
            <div className="mgh-chip-icon" style={{ background: "#ffe4e6", color: "#e11d48" }}>
              <FiThumbsUp />
            </div>
            <div className="mgh-chip-text">
              <strong>Thank You!</strong>
              <span>We appreciate your time and valuable feedback.</span>
            </div>
          </div>
        </div>

        {/* Contact Strip */}
        <ContactStrip />
      </div>

      {/* Unchanged Footer */}
      <Footer />
    </div>
  );
};

export default RequestaProblem;